import type { GroupId } from "@repo/app-types/entities/group";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { type FC, useMemo } from "react";
import { useAllTeammateGroupsQuery } from "../../api/entities/group";
import { TeammateGroupFilterItem } from "./teammateGroupFilterItem";

export interface TeammateGroupFilterItemContainerProps {
  // When set, restricts the dropdown to groups the teammate is a member of.
  // Used by teammate-scoped pages so users can't pick a group that would
  // yield empty data.
  teammateId?: TeammateId;
  // `undefined` reflects the not-yet-loaded preference state; `null` means
  // "All teammates"; an array (possibly empty) is the multi-select.
  value: readonly GroupId[] | null | undefined;
  onChange: (newValue: readonly GroupId[] | null) => void;
}

const allTeammateGroupsLabel = "All teammates";

export const TeammateGroupFilterItemContainer: FC<
  TeammateGroupFilterItemContainerProps
> = (props) => {
  const { data: allGroups } = useAllTeammateGroupsQuery();

  const teammateId = props.teammateId;
  const groups = useMemo(() => {
    if (allGroups === undefined) {
      return undefined;
    }
    if (teammateId === undefined) {
      return allGroups;
    }
    return allGroups.filter((g) => g.teammate_ids.includes(teammateId));
  }, [allGroups, teammateId]);

  // Loading state — render a disabled picker that mirrors the default
  // "All teammates" option.
  if (props.value === undefined || groups === undefined) {
    return (
      <TeammateGroupFilterItem
        allTeammateGroupsLabel={allTeammateGroupsLabel}
        value={null}
        groups={groups ?? []}
        onChange={() => {}}
        isDisabled
      />
    );
  }

  // Empty state (groups loaded but none exist, and no group is selected)
  if (props.value === null && groups.length === 0) {
    return null;
  }

  return (
    <TeammateGroupFilterItem
      allTeammateGroupsLabel={allTeammateGroupsLabel}
      value={props.value}
      groups={groups}
      onChange={props.onChange}
    />
  );
};
